import { AxiosRequestConfig } from 'axios';
import { AttributeI, AttributeOptionI, AttributePatch } from '~src/interfaces/attributes.types';
import { Segment } from '~src/interfaces/segment.interface';
import { AttributeAddI, AttributesRespI, AttrSortingType } from '~src/modules/attributes/interfaces';
import {
  AddAttribute,
  AddValue,
  ChangeAttrOption,
  DeleteAttribute,
  DeleteValue,
  EditAttribute,
  EditAttributePrivacy,
  EditValue,
  GetAllAttributes,
  GetAttributes,
  GetAttributesSearch,
  GetSingleAttribute,

  NEW_VALUE_LIST,
  RESET_ATTRIBUTES,
} from '~src/modules/attributes/constants/actionTypes';
import { baseApi, defaultBaseApi } from '~src/services/ApiController';
import { AsyncAction } from '~src/services/apiMiddleware';

export interface EditAttrFormDataI {
  name?: { [key: string]: string };
  description?: { [key: string]: string };
  hide_from_users?: boolean;
}

interface AttributeOptionsI {
  [key: string]: { [key: string]: string };
}
export interface AttrOptionAddI {
  attributes: AttributeOptionsI;
  users?: string[];
}

export interface AttrOptionAddFormI {
  attributes: { [key: string]: string };
  users?: Segment[];
}

export interface AttrOptionEditI {
  attribute_options: AttributeOptionsI;
  users?: string[];
  segments?: string[];
}

export interface AttrOptionEditFormI {
  option: { [key: string]: string };
  users?: Segment[];
}

export interface FetchAttributesParams {
  'filter.type'?: string;
  'filter.name'?: string;
  include?: string;
  page?: number;
  per_page?: number;
  sort?: AttrSortingType;
}

export const fetchAttributesList = (): AsyncAction<AttributeI[], never> => ({
  types: GetAllAttributes,
  callAPI: () => defaultBaseApi.getAttributes(),
});

export const fetchAttributes = (
  params: FetchAttributesParams = {},
  loadMore?: boolean,
  isSearch?: boolean,
  config?: AxiosRequestConfig,
): AsyncAction<AttributesRespI, { loadMore?: boolean }> => ({
  // TODO: consider to create separate action for filtered attributes
  types: isSearch ? GetAttributesSearch : GetAttributes,
  callAPI: () => defaultBaseApi.getAttributesPaginated(params, config),
  payload: { loadMore },
});

export const fetchSingleAttribute = (attributeId: string): AsyncAction<AttributeI, {attributeId: string}> => ({
  types: GetSingleAttribute,
  callAPI: () => baseApi.get(`/attributes/${attributeId}`),
  payload: { attributeId },
});

export const addAttribute = (
  attribute: AttributeAddI,
  defaultContentLocale: string,
): AsyncAction<AttributeI, AttributeAddI> => {
  const postData = {
    internal_type: attribute.attributeType,
    name: {
      [defaultContentLocale]: attribute.attributeName,
    },
  };
  return {
    types: AddAttribute,
    callAPI: () => defaultBaseApi.postAttribute(postData),
    payload: attribute,
  };
};

export const editAttribute = (id: string, attribute: Partial<AttributePatch>) => ({
  types: EditAttribute,
  callAPI: () => defaultBaseApi.patchAttribute(id, attribute),
  payload: { attribute },
});

export const changeAttributePrivacy = (id: string, isPrivate: boolean) => ({
  types: EditAttributePrivacy,
  callAPI: () => defaultBaseApi.patchAttribute(id, { hide_from_users: isPrivate }),
  payload: { isPrivate },
});

export const deleteAttribute = ({ uuid, slug }: AttributeI) => ({
  types: DeleteAttribute,
  callAPI: () => defaultBaseApi.deleteAttribute(uuid),
  payload: { attributeId: uuid, slug },
});

export const setNewAttributeOptionList = (attributeId: string, newList: AttributeOptionI[]) => ({
  type: NEW_VALUE_LIST,
  newList,
  attributeId,
});

export const changeOptionPosition = (optionId: string, sendItem: { position: number }) => ({
  types: ChangeAttrOption,
  callAPI: () => baseApi.patch(`/options/${optionId}`, sendItem),
  payload: { optionId, sendItem },
});

export const addAttributeOptionSecure = (payload: AttrOptionAddI): AsyncAction<string[], AttrOptionAddI> => ({
  types: AddValue,
  callAPI: () => baseApi.post('/users/batch-attributes-empty', payload),
  payload: { ...payload },
});

export const addAttributeOptionForce = (payload: AttrOptionAddI): AsyncAction<string[], AttrOptionAddI> => ({
  types: AddValue,
  callAPI: () => baseApi.post('/users/batch-attributes', payload),
  payload: { ...payload },
});

export interface EditAttrOptPayloadI extends AttrOptionEditI {
  attributeId: string;
}
export const editAttributeOptionSecure = (
  payload: AttrOptionEditI,
  attributeId: string,
): AsyncAction<AttributeOptionI, EditAttrOptPayloadI> => ({
  types: EditValue,
  callAPI: () => baseApi.patch('/users/batch-attributes-empty', payload),
  payload: { ...payload, attributeId },
});

export const editAttributeOptionForce = (
  payload: AttrOptionEditI,
  attributeId: string,
): AsyncAction<AttributeOptionI, EditAttrOptPayloadI> => ({
  types: EditValue,
  callAPI: () => baseApi.patch('/users/batch-attributes', payload),
  payload: { ...payload, attributeId },
});

export interface DeleteAttrOptPayload {
  attributeId: string;
  valueId: string;
  name: { [key: string]: string };
}
export const deleteAttributeOption = (
  attributeId: string,
  option: AttributeOptionI,
): AsyncAction<void, DeleteAttrOptPayload> => ({
  types: DeleteValue,
  callAPI: () => baseApi.delete(`/options/${option.uuid}`),
  payload: { attributeId, valueId: option.uuid, name: option.value },
});

export function resetAttributes() {
  return { type: RESET_ATTRIBUTES };
}
